import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Table,
  Badge,
  Button,
  Spinner,
} from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import PurchaseReturnService from "../../../services/OrderingServices/PurchaseReturn";
import NumberWithCommas from "../../../pages/Pages/Starter/NumberWithCommas";

const statusColor = (status) => {
  switch (status?.toLowerCase()) {
    case "approved": return "success";
    case "rejected": return "danger";
    case "pending": return "warning";
    default: return "secondary";
  }
};

const PurchaseReturnDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        const res = await PurchaseReturnService.detail(id);
        setData(res);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [id]);

  const breadcrumbItems = [
    { title: "Back to List", onClick: () => navigate("/purchase-returns") },
  ];

  if (loading) {
    return (
      <div className="page-content d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
        <Spinner color="primary" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center text-muted py-5">
            <i className="bx bx-error-circle fs-1 mb-2 d-block" />
            Return not found.
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Purchase Return Detail"
          pageTitle="Purchase Returns"
          breadcrumbItems={breadcrumbItems}
        />

        {/* Header Card */}
        <Card className="shadow-sm border-0 mb-3">
          <CardBody>
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h4 className="fw-bold text-primary mb-1">
                  {data.returnReferenceNo}
                </h4>
                <div className="text-muted small">
                  <i className="bx bx-calendar me-1" />
                  {data.returnDate_Nep
                    ? `${data.returnDate_Nep} (BS)`
                    : new Date(data.returnDate).toLocaleDateString()}
                  &nbsp;|&nbsp;
                  <span>
                    Created by:{" "}
                    <span className="fw-semibold">{data.createdBy}</span>
                  </span>
                </div>
              </div>
              <Badge
                color={statusColor(data.status)}
                className="px-3 py-2 fs-6 rounded-pill"
              >
                {data.status}
              </Badge>
            </div>
          </CardBody>
        </Card>

        {/* Info Grid */}
        <Row className="g-3 mb-3">
          <Col md={3}>
            <Card className="border-0 shadow-sm h-100">
              <CardBody className="text-center">
                <div className="text-muted small mb-1">Supplier</div>
                <div className="fw-bold">{data.supplierName}</div>
              </CardBody>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm h-100">
              <CardBody className="text-center">
                <div className="text-muted small mb-1">Purchase Ref</div>
                <div className="fw-bold">{data.purchaseReferenceNo}</div>
              </CardBody>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm h-100">
              <CardBody className="text-center">
                <div className="text-muted small mb-1">Total Return Amount</div>
                <div className="fw-bold text-success fs-5">
                  Rs. <NumberWithCommas number={data.totalReturnAmount} />
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm h-100">
              <CardBody className="text-center">
                <div className="text-muted small mb-1">Items</div>
                <div className="fw-bold fs-5">{data.items?.length || 0}</div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Remarks */}
        {data.remarks && (
          <Card className="border-0 shadow-sm mb-3">
            <CardBody>
              <div className="text-muted small mb-1">Remarks</div>
              <div>{data.remarks}</div>
            </CardBody>
          </Card>
        )}

        {/* Items Table */}
        <Card className="border-0 shadow-sm mb-3">
          <CardBody>
            <h5 className="fw-bold text-primary mb-3">
              <i className="bx bx-list-ul me-2" />
              Return Items
            </h5>
            <div className="table-responsive">
              <Table bordered hover className="align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Product</th>
                    <th>Unit</th>
                    <th className="text-end">Quantity</th>
                    <th className="text-end">Unit Price</th>
                    <th className="text-end">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items?.map((item, idx) => (
                    <tr key={item.id}>
                      <td>{idx + 1}</td>
                      <td className="fw-semibold">{item.productName}</td>
                      <td>{item.unitName}</td>
                      <td className="text-end">{item.quantity}</td>
                      <td className="text-end">
                        Rs. <NumberWithCommas number={item.unitPrice} />
                      </td>
                      <td className="text-end fw-bold text-success">
                        Rs. <NumberWithCommas number={item.totalPrice} />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="table-light">
                  <tr>
                    <td colSpan={5} className="text-end fw-bold">
                      Total Return Amount
                    </td>
                    <td className="text-end fw-bold text-success fs-6">
                      Rs. <NumberWithCommas number={data.totalReturnAmount} />
                    </td>
                  </tr>
                </tfoot>
              </Table>
            </div>
          </CardBody>
        </Card>

        <div className="d-flex justify-content-end">
          <Button
            color="secondary"
            outline
            onClick={() => navigate("/purchase-returns")}
          >
            <i className="bx bx-arrow-back me-1" />
            Back to List
          </Button>
        </div>
      </Container>
    </div>
  );
};

export default PurchaseReturnDetail;
